/**
 * LandingHeader — 랜딩페이지 상단 광고바 + 헤더 (Figma 2828:1844 · 2828:1805)
 *
 * 시안에서는 Hero 프레임 안에 들어 있지만, 그대로 두면 FAQ·문의 탭으로 전환했을 때
 * 헤더가 사라진다. 그래서 헤더만 분리해 SplashPage가 항상 렌더하고,
 * HeroSection이 음수 마진으로 배경을 헤더 뒤까지 깔아 시안과 같은 겹침을 만든다.
 *
 * 헤더는 sticky라 스크롤 내내 상단에 붙는다. 광고바는 함께 붙이지 않는다 —
 * 닫을 수 있는 안내 배너라 계속 자리를 차지하면 화면만 좁아진다.
 * 시안에서 헤더 배경은 투명이지만, 스크롤해 콘텐츠 위로 올라오면 글자가 겹쳐 읽히지 않는다.
 * 그래서 최상단에서만 투명이고 스크롤을 시작하면 흰 배경 + 하단 보더로 바뀐다.
 *
 * 색은 landing.css의 --lp-* 변수만 쓴다 (D-07 §1.2 랜딩페이지 예외).
 */
import { useEffect, useState } from 'react';
import type { LandingTab } from './tabs';
import { LANDING_TABS } from './tabs';
import { isMobileBrowser } from '../../lib/platform';

interface Props {
  activeTab: LandingTab;
  onTabChange: (tab: LandingTab) => void;
  onStart: () => void;
  /** 회원가입 버튼 전용 핸들러 — onStart(스튜디오 입장하기)와 목적지가 분리됐다 */
  onSignup: () => void;
  /** 광고바 노출 여부 — 닫으면 Hero 가 그 높이만큼 커진다(--lp-ad-h) */
  adOpen: boolean;
  onCloseAd: () => void;
}

/** 이 픽셀 이상 내려가면 헤더에 배경을 깐다 */
const SCROLL_THRESHOLD = 8;

export function LandingHeader({ activeTab, onTabChange, onStart, onSignup, adOpen, onCloseAd }: Props) {
  const [scrolled, setScrolled] = useState(false);
  // 모바일 브라우저에서만 앱설치 버튼을 노출한다. navigator 접근은 마운트 후로 미뤄
  // 서버·프리렌더 환경에서도 안전하게 둔다(현재 SPA라 실질 영향은 없다).
  const [showAppInstall, setShowAppInstall] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > SCROLL_THRESHOLD);
    onScroll(); // 새로고침으로 중간 위치에서 시작하는 경우 대비
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setShowAppInstall(isMobileBrowser());
  }, []);

  // 앱설치 유도 — 고정 딥링크 /u/app 으로 "전체 페이지 이동"시킨다(SPA navigate 는 App Links 를
  // 트리거하지 못한다). 앱 설치+App Links 검증된 Android 는 OS 가 가로채 앱이 열리고,
  // 미설치·iOS 는 웹 착지(AppLandingPage, 준비 중 안내)로 폴백된다.
  const goAppInstall = () => {
    window.location.href = `${window.location.origin}/u/app`;
  };

  return (
    <>
      {/* ── 광고바 (2828:1844) — sticky 아님. 스크롤하면 위로 사라진다 ────── */}
      {adOpen && (
        <div className="relative flex h-[var(--lp-ad-h)] shrink-0 items-center justify-center bg-black px-6">
          <p className="text-center text-sm leading-5 text-white">
            현재 오픈 베타 운영 중입니다 · 베타 기간 무료 이용
          </p>
          <button
            type="button"
            onClick={onCloseAd}
            aria-label="베타 안내 배너 닫기"
            className="absolute right-4 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center text-white opacity-80 transition-opacity hover:opacity-100"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      )}

      {/* ── 헤더 (2828:1805) — 스크롤 내내 상단 고정 ────────────────────── */}
      <header
        className={`sticky top-0 z-sticky w-full shrink-0 transition-colors duration-200 ${
          scrolled
            ? 'border-b border-[var(--lp-border)] bg-white/85 backdrop-blur'
            : 'border-b border-transparent'
        }`}
      >
        <div className="mx-auto flex w-full max-w-[1632px] items-center justify-between px-6 py-5">
          {/* 로고 + BETA */}
          <div className="flex shrink-0 items-center gap-2">
            <img src="/brand/indipia-logo.svg" alt="INDIPIA" className="h-[33px] w-auto" />
            <span className="rounded-full bg-[var(--lp-brand-subtle)] px-2 py-0.5 text-[11px] font-semibold leading-none text-[var(--lp-brand)]">
              BETA
            </span>
          </div>

          {/* 탭 내비게이션 — 외형은 시안, 동작은 기존 탭 전환 로직 */}
          <nav className="flex items-center gap-8">
            {LANDING_TABS.map((tab) => {
              const active = tab === activeTab;
              return (
                <button
                  key={tab}
                  type="button"
                  onClick={() => onTabChange(tab)}
                  aria-current={active ? 'page' : undefined}
                  className={
                    active
                      ? 'text-base font-semibold leading-none text-[var(--lp-brand)]'
                      : 'text-base leading-none text-[var(--lp-text-muted)] transition-colors hover:text-[var(--lp-brand)]'
                  }
                >
                  {tab}
                </button>
              );
            })}
          </nav>

          {/* 앱 설치(모바일 전용) + 회원가입 + 스튜디오 입장 */}
          <div className="flex shrink-0 items-center gap-8">
            {showAppInstall && (
              <button
                type="button"
                onClick={goAppInstall}
                className="flex items-center gap-1.5 text-base leading-none text-[var(--lp-text-muted)] transition-colors hover:text-[var(--lp-brand)]"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <path d="M12 3v12" />
                  <polyline points="7 10 12 15 17 10" />
                  <path d="M5 21h14" />
                </svg>
                앱 설치
              </button>
            )}
            <button
              type="button"
              onClick={onSignup}
              className="text-base leading-none text-[var(--lp-text-muted)] transition-colors hover:text-[var(--lp-brand)]"
            >
              회원가입
            </button>
            <button type="button" onClick={onStart} className="lp-btn-brand">
              스튜디오 입장하기
              <img src="/landing/ic-arrow.svg" alt="" className="size-5" />
            </button>
          </div>
        </div>
      </header>
    </>
  );
}
